import React, { useRef, useEffect, useState } from "react";
 

/**
 * AnimatedText
 * Splits text into words/characters and reveals them with a staggered
 * translateY + opacity animation when scrolled into view.
 *
 * Usage:
 *   <AnimatedText as="h2" className="common-section-heading-h2" text="Specialised Services" />
 *
 *   <AnimatedText
 *     as="h1"
 *     className="common-banner-heading-h1"
 *     lines={["PEACE OF MIND.", "DELIVERED."]}
 *   />
 *
 *   <AnimatedText
 *     as="p"
 *     className="common-paragraph-p"
 *     text="Some paragraph copy here."
 *     splitBy="word"   // paragraphs read better animating by word, not char
 *     stagger={0.02}
 *   />
 *
 * Props:
 *  - text      : string  (single line of text)
 *  - lines     : string[] (use instead of `text` when you need <br /> between lines,
 *                          e.g. banner headings)
 *  - as        : string  ('h1' | 'h2' | 'p' | 'span' ...) — the tag to render, default 'span'
 *  - className : string  — your existing class (common-banner-heading-h1 etc.) is preserved
 *  - splitBy   : 'char' | 'word' — animate per character (headings) or per word (paragraphs)
 *  - stagger   : number  — seconds between each char/word's animation start
 *  - delay     : number  — seconds before the whole animation starts
 *  - threshold : number  — IntersectionObserver visibility threshold (0-1)
 *  - once      : boolean — if false, re-animates every time it re-enters viewport
 */
const AnimatedText = ({
  text = "",
  lines = null,
  as: Tag = "span",
  className = "",
  splitBy = "char",
  stagger = 0.03,
  delay = 0,
  threshold = 0.2,
  once = true,
}) => {
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (once) observer.unobserve(el);
        } else if (!once) {
          setIsVisible(false);
        }
      },
      { threshold }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [once, threshold]);

  const sourceLines = lines && lines.length ? lines : [text];

  // running index so stagger is continuous across the whole block,
  // not restarted per line/word
  let globalIndex = 0;

  const renderWord = (word, wKey) => {
    if (splitBy === "word") {
      const idx = globalIndex++;
      return (
        <span className="word" key={wKey}>
          <span
            className="char"
            style={{ transitionDelay: `${delay + idx * stagger}s` }}
          >
            {word}
          </span>
        </span>
      );
    }

    return (
      <span className="word" key={wKey}>
        {[...word].map((char, cIdx) => {
          const idx = globalIndex++;
          return (
            <span
              className="char"
              key={cIdx}
              style={{ transitionDelay: `${delay + idx * stagger}s` }}
            >
              {char}
            </span>
          );
        })}
      </span>
    );
  };

  return (
    <Tag
      ref={containerRef}
      className={`animated-text ${className} ${
        isVisible ? "animated-text--visible" : ""
      }`}
      aria-label={sourceLines.join(" ")}
    >
      {sourceLines.map((line, lIdx) => {
        // Collapse any raw newlines / repeated whitespace that sneak in
        // from multi-line JSX string literals (very common with long
        // paragraph copy written across several source lines).
        const words = line.trim().split(/\s+/).filter(Boolean);

        return (
          <React.Fragment key={lIdx}>
            {words.map((word, wIdx) => (
              <React.Fragment key={wIdx}>
                {renderWord(word, `${lIdx}-${wIdx}`)}
                {/* a REAL space here (not \u00A0) so the browser can still
                    wrap the line normally between words */}
                {wIdx !== words.length - 1 && " "}
              </React.Fragment>
            ))}
            {lIdx !== sourceLines.length - 1 && <br />}
          </React.Fragment>
        );
      })}
    </Tag>
  );
};

export default AnimatedText;